import React, { useState, useEffect } from "react";
import {
  Search,
  X,
  Building,
  MapPinned,
  CheckCircle,
  Car,
  Clock,
  ChevronDown,
  ChevronRight,
  Plus,
} from "lucide-react";
import { Icon } from "@iconify/react";
import { useNavigate, useLocation } from "react-router-dom";
import useDebounce from "../hook/useDebounce";
import { api, branchApi } from "../Apis/Api";
import Breadcrumb from "../components/Breadcrumb";
import ErrorAlert from "../components/ui/ErrorAlert";
import { usePermissions } from "../context/PermissionContext";

const CountsCard = ({ counts, loading }) => {
  const { branches_count, locations_count, slots_count, total_workflows } =
    counts || {};

  const renderCard = (icon, count, label, gradientClass) => (
    <div className="col-lg-3 col-md-6 col-sm-12">
      <div className={`card shadow-none border ${gradientClass} h-100`}>
        <div className="card-body p-20">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <p className="fw-medium text-primary-light mb-1">{label}</p>
              {loading ? (
                <div className="placeholder-glow">
                  <span className="placeholder col-6"></span>
                </div>
              ) : (
                <h6 className="mb-0">{count || 0}</h6>
              )}
            </div>
            <div className="w-50-px h-50-px bg-primary-600 rounded-circle d-flex justify-content-center align-items-center">
              {loading ? (
                <div className="placeholder-glow">
                  <span className="placeholder"></span>
                </div>
              ) : (
                <Icon icon={icon} className="text-white text-2xl mb-0" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="row g-20 mb-24">
      {renderCard(
        "lucide:workflow",
        total_workflows,
        "Total Workflows",
        "bg-gradient-start-1"
      )}
      {renderCard(
        "solar:buildings-bold-duotone",
        branches_count,
        "Total Branches",
        "bg-gradient-start-2"
      )}
      {renderCard(
        "fluent:location-ripple-20-regular",
        locations_count,
        "Total Locations",
        "bg-gradient-start-3"
      )}
      {renderCard(
        "streamline:parking-sign-remix",
        slots_count,
        "Total Slots",
        "bg-gradient-start-4"
      )}
    </div>
  );
};

const BranchesHierarchyPage = () => {
  const [expandedBranches, setExpandedBranches] = useState({});
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [counts, setCounts] = useState({
    branches_count: 0,
    locations_count: 0,
    slots_count: 0,
  });
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [error, setError] = useState(null);
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Handle hover state
  const handleHover = (locationId, type) => {
    setHoveredItem({ id: locationId, type });
  };

  const handleLeave = () => {
    setHoveredItem(null);
  };

  // Fetch branches
  useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        setError(null);
        console.log("🔥 Fetching branches...");
        const response = await branchApi.getClientBranches({
          sort_by: "branch_name",
          sort_order: "asc",
          want_pagination: 0,
          search: debouncedSearchQuery,
        });
        console.log("🔥 Branches response:", response);

        setBranches(response.data);
        setFilteredBranches(response.data);

        if (response.counts) {
          setCounts({
            branches_count: response.counts.branches_count || 0,
            locations_count: response.counts.locations_count || 0,
            slots_count: response.counts.slots_count || 0,
            total_workflows: response.counts.total_workflows || 0,
          });
        }

        // Check for expandedBranchId from navigation state
        const expandedBranchId = location.state?.expandedBranchId;
        if (expandedBranchId) {
          setExpandedBranches({ [expandedBranchId]: true });
          fetchLocations(expandedBranchId); // Fetch locations for the expanded branch
        }
      } catch (error) {
        setError(true);
        console.error("🔥 Error fetching branches:", error);
      }
      setLoadingBranches(false);
      setIsSearching(false);
    };

    fetchBranches();
  }, [debouncedSearchQuery, location.state]);

  // Handle search input
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsSearching(true);

    if (query.trim() === "") {
      setFilteredBranches(branches);
    } else {
      const filtered = branches.filter((branch) =>
        branch.branch_name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredBranches(filtered);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setFilteredBranches(branches);
    setIsSearching(false);
  };

  // Fetch locations for a branch
  const fetchLocations = async (branchId) => {
    console.log("🔥 fetchLocations called with branchId:", branchId);
    setLoadingLocations(true);
    try {
      const params = {
        sort_by: "location_name",
        per_page: 100,
      };
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        queryParams.append(key, params[key]);
      });

      const response = await api.get(
        `/get-branch-locations/${branchId}?${queryParams.toString()}`
      );
      console.log("🔥 Locations response:", response);

      const locationData = response.data.data || [];
      setLocations(locationData);
    } catch (error) {
      console.error("🔥 Error fetching locations:", error);
      setLocations([]);
    }
    setLoadingLocations(false);
  };

  // Toggle branch expansion
  const toggleBranch = (branchId) => {
    const isExpanding = !expandedBranches[branchId];

    if (isExpanding) {
      setExpandedBranches({ [branchId]: true });
      fetchLocations(branchId);
    } else {
      setExpandedBranches((prev) => ({
        ...prev,
        [branchId]: false,
      }));
      setLocations([]);
    }
  };

  // Shimmer loading component
  const ShimmerCard = ({ type }) => {
    const shimmerCount = type === "branches" ? 3 : type === "locations" ? 2 : 4;
    return (
      <div className="row g-20">
        {Array(shimmerCount)
          .fill()
          .map((_, index) => (
            <div key={index} className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
              <div className="card border-0 shadow-sm radius-12 h-100">
                <div className="card-body p-20">
                  <div className="d-flex align-items-start gap-3">
                    <div className="placeholder-glow">
                      <div className="placeholder w-48-px h-48-px radius-8"></div>
                    </div>
                    <div className="flex-grow-1">
                      <div className="placeholder-glow mb-2">
                        <span className="placeholder col-8"></span>
                      </div>
                      <div className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    );
  };

  // Render popup for workflows
  const renderWorkflowPopup = (location, locationId) => {
    if (!location.assigned_workflows?.length) return null;
    const isVisible =
      hoveredItem?.id === locationId && hoveredItem.type === "workflow";

    return (
      <div
        className="position-absolute rounded popup-content"
        style={{
          backgroundColor: "#333",
          color: "white",
          padding: "8px",
          borderRadius: "4px",
          fontSize: "12px",
          bottom: "100%", // Changed from top to bottom
          left: "0",
          minWidth: "200px",
          zIndex: isVisible ? 9999 : -1,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          marginBottom: "4px", // Changed from marginTop to marginBottom
          maxHeight: "150px",
          overflowY: "auto",
          opacity: isVisible ? 1 : 0,
          visibility: isVisible ? "visible" : "hidden",
        }}
      >
        <ul className="list-unstyled mb-0">
          {location.assigned_workflows.map((wf) => (
            <li key={wf.workflow_id} style={{ marginBottom: "4px" }}>
              {wf.workflow_name}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderStaffPopup = (location, locationId) => {
    if (!location.assigned_staff?.length) return null;
    const isVisible =
      hoveredItem?.id === locationId && hoveredItem.type === "staff";

    return (
      <div
        className="position-absolute rounded popup-content"
        style={{
          backgroundColor: "#333",
          color: "white",
          padding: "8px",
          borderRadius: "4px",
          fontSize: "12px",
          bottom: "100%", // Changed from top to bottom
          left: "33.33%",
          transform: "translateX(-50%)",
          minWidth: "200px",
          zIndex: isVisible ? 9999 : -1,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          marginBottom: "4px", // Changed from marginTop to marginBottom
          maxHeight: "150px",
          overflowY: "auto",
          opacity: isVisible ? 1 : 0,
          visibility: isVisible ? "visible" : "hidden",
        }}
      >
        <ul className="list-unstyled mb-0">
          {location.assigned_staff.map((staff) => (
            <li key={staff.staff_id} style={{ marginBottom: "4px" }}>
              {staff.staff_name}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderSlotsPopup = (location, locationId) => {
    const isVisible =
      hoveredItem?.id === locationId && hoveredItem.type === "slots";

    return (
      <div
        className="position-absolute rounded popup-content"
        style={{
          backgroundColor: "#333",
          color: "white",
          padding: "8px",
          borderRadius: "4px",
          fontSize: "12px",
          bottom: "100%", // Changed from top to bottom
          left: "66.66%",
          transform: "translateX(-75%)",
          minWidth: "200px",
          zIndex: isVisible ? 9999 : -1,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          marginBottom: "4px", // Changed from marginTop to marginBottom
          opacity: isVisible ? 1 : 0,
          visibility: isVisible ? "visible" : "hidden",
        }}
      >
        <div className="d-flex align-items-center mb-6">
          <CheckCircle
            size={12}
            className="me-8"
            style={{ color: "#10b981" }}
          />
          <span style={{ color: "white" }}>
            Available: {location.open_slots || 0}
          </span>
        </div>
        <div className="d-flex align-items-center mb-6">
          <Car size={12} className="me-8" style={{ color: "#ef4444" }} />
          <span style={{ color: "white" }}>
            Occupied: {location.occupied_slots || 0}
          </span>
        </div>
        <div className="d-flex align-items-center">
          <Clock size={12} className="me-8" style={{ color: "#f59e0b" }} />
          <span style={{ color: "white" }}>
            Reserved: {location.reserved_slots || 0}
          </span>
        </div>
      </div>
    );
  };
  if (error) {
    return <ErrorAlert error={error} />;
  }

  return (
    <>
      {" "}
      <Breadcrumb title="Branches Hierarchy" />
      <div
        style={{
          backgroundColor: "var(--bg-color)",
          minHeight: "100vh",
          // padding: "var(--size-32)",
        }}
      >
        {/* Counts Card */}
        <div className="container-fluid">
          <CountsCard counts={counts} loading={loadingBranches} />
        </div>

        {/* Search Bar */}
        <div className="container-fluid mb-24">
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm radius-12">
                <div className="card-body p-16">
                  <div className="position-relative">
                    {/* <div className="position-absolute top-50 start-0 translate-middle-y ps-16">
                    <Search size={20} className="text-secondary-light" />
                  </div> */}
                    <input
                      type="text"
                      className="form-control form-control-lg ps-48 pe-48"
                      placeholder="Search branches by name..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                    {searchQuery && (
                      <div className="position-absolute top-50 end-0 translate-middle-y pe-16">
                        <button
                          className="btn btn-sm btn-icon"
                          onClick={clearSearch}
                        >
                          <X size={16} className="text-secondary-light" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Branches Grid */}
        <div className="container-fluid">
          {loadingBranches || isSearching ? (
            <ShimmerCard type="branches" />
          ) : filteredBranches.length === 0 ? (
            <div className="text-center py-48">
              <div className="w-80-px h-80-px bg-neutral-100 radius-12 d-flex justify-content-center align-items-center mx-auto mb-16">
                <Search size={32} className="text-secondary-light" />
              </div>
              <h5 className="text-secondary-light mb-8">No branches found</h5>
              <p className="text-secondary-light mb-0">
                {searchQuery
                  ? `No branches match your search for "${searchQuery}"`
                  : "No branches available"}
              </p>
              {hasPermission("add_client_branch") && (
                <button
                  className="btn btn-smaller btn-primary-600 mt-16"
                  onClick={() => navigate("/branch-create")}
                >
                  Create Branch
                </button>
              )}
            </div>
          ) : (
            <div className="branches-container">
              {filteredBranches.map((branch) => (
                <div key={branch.branch_id} className="branch-card">
                  <div
                    className="card border-lg shadow-lg radius-16 overflow-hidden"
                    style={{
                      background:
                        "linear-gradient(135deg, var(--primary-50) 0%, var(--primary-25) 100%)",
                      border: "none",
                    }}
                  >
                    <div
                      className="position-relative overflow-hidden bg-gradient-to-r from-primary-50 to-primary-25 cursor-pointer branch-header"
                      style={{
                        background:
                          "linear-gradient(135deg, var(--primary-50) 0%, var(--primary-25) 100%)",
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => toggleBranch(branch.branch_id)}
                    >
                      <div className="card-body p-20">
                        <div className="d-flex align-items-start justify-content-between">
                          <div className="d-flex align-items-start gap-3 flex-grow-1">
                            <div className="w-48-px h-48-px bg-primary-600 radius-12 d-flex justify-content-center align-items-center position-relative overflow-hidden flex-shrink-0">
                              <Building className="text-white" size={20} />
                              <div
                                className="position-absolute top-0 start-0 w-100 h-100"
                                style={{
                                  background:
                                    "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)",
                                }}
                              ></div>
                            </div>
                            <div className="flex-grow-1 min-w-0">
                              <p className="text-primary-light fw-bold mb-8 fs-16">
                                {branch.branch_name}
                              </p>
                              <span
                                className={`badge px-12 py-4 fs-12 fw-medium radius-6 ${branch.location_count > 0
                                    ? "bg-primary-600 text-white"
                                    : "bg-neutral-300 text-neutral-600"
                                  }`}
                              >
                                {branch.location_count > 0
                                  ? `${branch.location_count} Location${branch.location_count > 1 ? "s" : ""
                                  }`
                                  : "No Locations"}
                              </span>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {/* Add Branch Button */}
                            {hasPermission("add_client_branch") && (
                              <button
                                className="btn btn-smaller btn-primary-600 d-flex align-items-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/branch-create");
                                }}
                              >
                                <Plus size={14} className="me-1" />
                                Add Branch
                              </button>
                            )}

                            {/* Expand/Collapse Button */}
                            <button
                              className="w-28-px h-28-px radius-6 d-flex justify-content-center align-items-center flex-shrink-0"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                              }}
                              onClick={() => toggleBranch(branch.branch_id)}
                            >
                              {expandedBranches[branch.branch_id] ? (
                                <ChevronDown
                                  size={16}
                                  className="text-primary-600"
                                  style={{ transition: "transform 0.3s ease" }}
                                />
                              ) : (
                                <ChevronRight
                                  size={16}
                                  className="text-primary-600"
                                  style={{ transition: "transform 0.3s ease" }}
                                />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Branch Content - Only show when expanded */}
                    {expandedBranches[branch.branch_id] && (
                      <div
                        className="location-expansion"
                        style={{
                          animation: expandedBranches[branch.branch_id]
                            ? "slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                            : "none",
                          padding: "var(--size-20)",
                          background:
                            "linear-gradient(135deg, var(--success-50) 0%, var(--success-25) 100%)",
                        }}
                      >
                        <div className="d-flex justify-content-end mb-16">
                          {/* Add Location Button */}
                          {hasPermission("add_branch_location") && (
                            <button
                              className="btn btn-smaller btn-success d-flex align-items-center"
                              onClick={() =>
                                navigate("/branch-location-create", {
                                  state: {
                                    branchData: {
                                      id: branch.branch_id,
                                      name: branch.branch_name,
                                      address: branch.address || "",
                                      country_name: branch.country_name || "",
                                    },
                                  },
                                })
                              }
                            >
                              <Plus size={14} className="me-1" />
                              Add Location
                            </button>
                          )}
                        </div>
                        {loadingLocations ? (
                          <ShimmerCard type="locations" />
                        ) : locations.length > 0 ? (
                          locations.map((location, index) => (
                            <div
                              key={location.location_id}
                              className="location-card"
                              style={{
                                marginBottom:
                                  index === locations.length - 1
                                    ? "0"
                                    : "var(--size-20)",
                                transition:
                                  "transform 0.2s ease, box-shadow 0.2s ease",
                              }}
                            >
                              <div
                                className="position-relative bg-gradient-to-r from-success-50 to-success-25 location-header"
                                style={{
                                  background:
                                    "linear-gradient(135deg, var(--success-50) 0%, var(--success-25) 100%)",
                                  transition: "all 0.2s ease",
                                  borderRadius: "var(--rounded-12)",
                                  padding: "var(--size-20)",
                                }}
                              >
                                <div className="d-flex align-items-start gap-3 flex-grow-1 position-relative">
                                  <div className="w-40-px h-40-px bg-success-600 radius-8 d-flex justify-content-center align-items-center flex-shrink-0">
                                    <MapPinned
                                      size={18}
                                      className="text-white"
                                    />
                                  </div>
                                  <div className="flex-grow-1 min-w-0">
                                    <p className="text-primary-light fw-semibold mb-12 fs-16 text-truncate">
                                      {location.location_name}
                                    </p>
                                    <div className="d-flex gap-12">
                                      {/* Workflow Box */}
                                      <div
                                        className="bg-white-alpha-20 p-12 rounded-8 flex-grow-1 position-relative d-flex flex-column"
                                        onMouseEnter={() =>
                                          handleHover(
                                            location.location_id,
                                            "workflow"
                                          )
                                        }
                                        onMouseLeave={handleLeave}
                                      >
                                        <div className="cursor-pointer flex-grow-1">
                                          <p className="fw-medium text-secondary-light mb-4 fs-12">
                                            Workflows (
                                            {location.assigned_workflows
                                              ?.length || 0}
                                            )
                                          </p>
                                        </div>
                                        {/* Assign Workflow Button */}
                                        {hasPermission("assign_workflow") && (
                                          <button
                                            className="btn btn-smaller btn-primary-600 d-flex align-items-center justify-content-center mt-auto"
                                            style={{
                                              fontSize: "10px",
                                              minHeight: "24px",
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigate("/workflow-assign", {
                                                state: {
                                                  locationData: {
                                                    id: location.location_id,
                                                    name: location.location_name,
                                                    branchId: branch.branch_id,
                                                    branchName:
                                                      branch.branch_name,
                                                  },
                                                },
                                              });
                                            }}
                                            onMouseEnter={(e) =>
                                              e.stopPropagation()
                                            }
                                            onMouseLeave={(e) =>
                                              e.stopPropagation()
                                            }
                                          >
                                            <Plus size={14} className="me-1" />
                                            Assign Workflow
                                          </button>
                                        )}

                                        {renderWorkflowPopup(
                                          location,
                                          location.location_id
                                        )}
                                      </div>

                                      {/* Staff Box */}
                                      <div
                                        className="bg-white-alpha-20 p-12 rounded-8 flex-grow-1 position-relative d-flex flex-column"
                                        onMouseEnter={() =>
                                          handleHover(
                                            location.location_id,
                                            "staff"
                                          )
                                        }
                                        onMouseLeave={handleLeave}
                                      >
                                        <div className="cursor-pointer flex-grow-1">
                                          <p className="fw-medium text-secondary-light mb-4 fs-12">
                                            Staff (
                                            {location.assigned_staff?.length ||
                                              0}
                                            )
                                          </p>
                                        </div>
                                        {/* Assign Staff Button */}
                                        {hasPermission(
                                          "assign_staff_branch"
                                        ) && (
                                            <button
                                              className="btn btn-smaller btn-success d-flex align-items-center justify-content-center mt-auto"
                                              style={{
                                                fontSize: "10px",
                                                minHeight: "24px",
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigate("/staff-branch-assign", {
                                                  state: {
                                                    stateData: {
                                                      branch_id: branch.branch_id,
                                                      location_id:
                                                        location.location_id,
                                                      branchName:
                                                        branch.branch_name,
                                                      locationName:
                                                        location.location_name,
                                                    },
                                                  },
                                                });
                                              }}
                                              onMouseEnter={(e) =>
                                                e.stopPropagation()
                                              }
                                              onMouseLeave={(e) =>
                                                e.stopPropagation()
                                              }
                                            >
                                              <Plus size={14} className="me-1" />
                                              Assign Staff
                                            </button>
                                          )}

                                        {renderStaffPopup(
                                          location,
                                          location.location_id
                                        )}
                                      </div>

                                      {/* Slots Box */}
                                      <div
                                        className="bg-white-alpha-20 p-12 rounded-8 flex-grow-1 position-relative"
                                        onMouseEnter={() =>
                                          handleHover(
                                            location.location_id,
                                            "slots"
                                          )
                                        }
                                        onMouseLeave={handleLeave}
                                      >
                                        <div className="cursor-pointer">
                                          <p className="fw-medium text-secondary-light mb-4 fs-12">
                                            Slots ({location.total_slots || 0})
                                          </p>
                                        </div>
                                        {renderSlotsPopup(
                                          location,
                                          location.location_id
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center">
                            <p className="text-secondary-light fs-16 mb-16">
                              No locations found
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <style jsx>{`
          :root {
            --size-4: 4px;
            --size-8: 8px;
            --size-12: 12px;
            --size-16: 16px;
            --size-20: 20px;
            --size-24: 24px;
            --size-32: 32px;
            --rounded-8: 8px;
            --rounded-12: 12px;
          }

          .branches-container {
            columns: 400px; /* Fixed width for each column */
            column-gap: var(--size-24);
            column-fill: balance; /* Distribute items evenly across columns */
          }

          .branch-card {
            break-inside: avoid; /* Prevent breaking inside a branch card */
            margin-bottom: var(--size-24);
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }

          .branch-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          }

          .branch-header:hover {
            background: linear-gradient(
              135deg,
              var(--primary-100) 0%,
              var(--primary-50) 100%
            ) !important;
          }

          .location-card {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }

          .location-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
          }

          .location-header:hover {
            background: linear-gradient(
              135deg,
              var(--success-100) 0%,
              var(--success-50) 100%
            ) !important;
          }

          .location-expansion {
            transform-origin: top;
          }

          .bg-white-alpha-20 {
            background-color: rgba(255, 255, 255, 0.2);
          }

          .popup-content {
            pointer-events: none;
          }

          .btn-smaller {
            padding: 3px 6px !important;
            font-size: 11px !important;
            line-height: 1.5;
          }

          .btn-smaller .me-1 {
            margin-right: 4px;
          }

          @keyframes slideDown {
            from {
              max-height: 0;
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              max-height: 2000px;
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Mobile View (<576px) */
          @media (max-width: 575.98px) {
            .container-fluid {
              padding-left: var(--size-12);
              padding-right: var(--size-12);
            }

            .branches-container {
              columns: 100%; /* Single column on mobile */
            }

            .row.g-20 {
              gap: var(--size-12) !important;
              margin-left: calc(-1 * var(--size-12));
              margin-right: calc(-1 * var(--size-12));
            }

            .col-sm-12 {
              padding-left: var(--size-12);
              padding-right: var(--size-12);
            }

            .mb-24 {
              margin-bottom: var(--size-16) !important;
            }

            .py-48 {
              padding-top: var(--size-24) !important;
              padding-bottom: var(--size-24) !important;
            }

            .card-body {
              padding: var(--size-12) !important;
            }

            .location-expansion {
              padding: var(--size-12) !important;
            }

            .location-card {
              margin-bottom: var(--size-12) !important;
            }

            .location-header {
              padding: var(--size-12) !important;
              borderradius: var(--rounded-8) !important;
            }

            .fs-16 {
              font-size: 14px !important;
            }

            .fs-12 {
              font-size: 10px !important;
            }

            .fs-18 {
              font-size: 16px !important;
            }

            .w-80-px {
              width: 48px !important;
              height: 48px !important;
            }

            .w-50-px {
              width: 36px !important;
              height: 36px !important;
            }

            .w-48-px {
              width: 36px !important;
              height: 36px !important;
            }

            .w-40-px {
              width: 32px !important;
              height: 32px !important;
            }

            .w-28-px {
              width: 24px !important;
              height: 24px !important;
            }

            .text-2xl {
              font-size: 18px !important;
            }

            .form-control-lg {
              font-size: 12px !important;
              padding: 6px 28px !important;
            }

            .ps-48 {
              padding-left: 28px !important;
            }

            .pe-48 {
              padding-right: 28px !important;
            }

            .ps-16 {
              padding-left: 12px !important;
            }

            .pe-16 {
              padding-right: 12px !important;
            }

            .placeholder.w-48-px {
              width: 36px !important;
              height: 36px !important;
            }

            .badge {
              padding: 2px 8px !important;
              font-size: 10px !important;
            }

            .mb-6 {
              margin-bottom: 4px !important;
            }

            .me-8 {
              margin-right: 4px !important;
            }

            .popup-content {
              font-size: 10px !important;
              padding: 4px !important;
              minwidth: 150px !important;
            }

            .btn-smaller {
              font-size: 10px !important;
              padding: 2px 6px !important;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default BranchesHierarchyPage;
